import Button from "@imminently/decisively-ui/Button";
import Form from "@imminently/decisively-ui/Form";
import Radio from "@imminently/decisively-ui/Radio";
import RadioButton from "@imminently/decisively-ui/RadioButton";
import RadioCard from "@imminently/decisively-ui/RadioCard";
import RadioGroup from "@imminently/decisively-ui/RadioGroup";
import RadioGroupField from "@imminently/decisively-ui/RadioGroupField";
import { Calculator, Clock3 } from "lucide-react";
import { useState } from "react";
import { ComponentPage, ComponentPreview, ComponentSection } from "../components/ComponentPage";

const options = [
  { value: "timesheet", label: "Timesheet", helperText: "Import workforce files." },
  { value: "award", label: "Award interpretation", helperText: "Calculate award results." },
];

const cardOptions = [
  {
    value: "timesheet",
    label: "Timesheet",
    description: "Import workforce, leave, and timesheet files into an existing rules project.",
    details: "Choose this when another system remains responsible for payroll processing.",
    icon: (
      <Clock3
        className="h-5 w-5"
        aria-hidden="true"
      />
    ),
  },
  {
    value: "award",
    label: "Award interpretation",
    description: "Calculate award results and prepare changed pay outcomes for return.",
    details: "Choose this when the project performs award interpretation.",
    icon: (
      <Calculator
        className="h-5 w-5"
        aria-hidden="true"
      />
    ),
  },
];

const formCardOptions = [
  {
    value: "cloud",
    label: "Cloud hosted",
    description: "Managed infrastructure and automatic updates.",
    icon: (
      <Clock3
        className="h-5 w-5"
        aria-hidden="true"
      />
    ),
  },
  {
    value: "self-hosted",
    label: "Self hosted",
    description: "Infrastructure managed within your environment.",
    icon: (
      <Calculator
        className="h-5 w-5"
        aria-hidden="true"
      />
    ),
  },
];

type RadioFormValues = {
  buttonStyle: string;
  cardStyle: string;
  standard: string;
};

export function RadioPage() {
  const [value, setValue] = useState("timesheet");
  const [submittedValues, setSubmittedValues] = useState<RadioFormValues | undefined>();

  return (
    <ComponentPage
      title="Radio"
      description="A native radio input for choosing one value from a small group."
    >
      <ComponentSection
        title="Standalone radios"
        description="Use fieldless radios outside forms when each option should be visible."
      >
        <ComponentPreview className="items-start">
          <fieldset className="grid gap-3 border-0 p-0">
            <legend className="mb-2 text-sm font-medium">Processing mode</legend>
            {options.map((option) => (
              <div
                key={option.value}
                className="flex items-center gap-2 text-sm"
              >
                <Radio
                  id={`processing-mode-${option.value}`}
                  name="processing-mode"
                  value={option.value}
                  checked={value === option.value}
                  onValueChange={setValue}
                />
                <label htmlFor={`processing-mode-${option.value}`}>{option.label}</label>
              </div>
            ))}
          </fieldset>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Radio cards"
        description="Use RadioCard inside RadioGroup when each option needs supporting detail."
      >
        <ComponentPreview className="items-stretch">
          <div className="w-full max-w-2xl">
            <RadioGroup
              ariaLabel="Processing mode"
              value={value}
              onValueChange={setValue}
            >
              {cardOptions.map((option) => (
                <RadioCard
                  key={option.value}
                  {...option}
                />
              ))}
            </RadioGroup>
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Radio cards in columns"
        description="Use columns for short card options when there is room."
      >
        <ComponentPreview className="items-stretch">
          <RadioGroup
            ariaLabel="Processing mode"
            columns={2}
            value={value}
            onValueChange={setValue}
          >
            <RadioCard
              value="timesheet"
              label="Timesheet"
              description="Import workforce files."
              icon={
                <Clock3
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              }
            />
            <RadioCard
              value="award"
              label="Award interpretation"
              description="Calculate award results."
              icon={
                <Calculator
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              }
            />
          </RadioGroup>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Radio buttons"
        description="Use RadioButton inside RadioGroup for compact, segmented choices."
      >
        <ComponentPreview className="items-start">
          <RadioGroup
            ariaLabel="Processing mode"
            variant="buttons"
            value={value}
            onValueChange={setValue}
          >
            <RadioButton value="timesheet">Timesheet</RadioButton>
            <RadioButton value="award">Award interpretation</RadioButton>
          </RadioGroup>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Radio group fields"
        description="Each RadioGroupField writes its own named value to the form."
      >
        <ComponentPreview className="items-start">
          <Form<RadioFormValues>
            className="grid w-full max-w-2xl gap-5"
            defaultValues={{
              standard: "timesheet",
              cardStyle: "cloud",
              buttonStyle: "account",
            }}
            onSubmit={setSubmittedValues}
          >
            <RadioGroupField
              name="standard"
              label="Standard radios"
              helperText="Field name: standard"
            >
              {options.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center gap-2 text-sm"
                >
                  <Radio
                    id={`standard-${option.value}`}
                    value={option.value}
                  />
                  <label htmlFor={`standard-${option.value}`}>{option.label}</label>
                </div>
              ))}
            </RadioGroupField>

            <RadioGroupField
              name="cardStyle"
              label="Radio cards"
              helperText="Field name: cardStyle"
            >
              {formCardOptions.map((option) => (
                <RadioCard
                  key={option.value}
                  {...option}
                />
              ))}
            </RadioGroupField>

            <RadioGroupField
              name="buttonStyle"
              label="Radio buttons"
              helperText="Field name: buttonStyle"
              variant="buttons"
            >
              <RadioButton value="account">Account</RadioButton>
              <RadioButton value="settings">Settings</RadioButton>
            </RadioGroupField>

            <div className="grid gap-3">
              <Button
                label="Submit radio form"
                type="submit"
              />
              <pre className="overflow-auto rounded-md bg-muted p-3 text-xs text-foreground">
                {JSON.stringify(submittedValues ?? {}, null, 2)}
              </pre>
            </div>
          </Form>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  );
}
